import { useEffect, useState } from 'react'
import type { StreamingPlayer } from './player/StreamingPlayer'

export interface TrackMeta {
  id: string
  label: string
  hint: string
}

/**
 * Transport for the StreamingPlayer: play/pause, a scrub bar that also shows how
 * much of the active track has been processed so far, and the A/B track switch.
 */
export function StreamPlayer({
  player,
  tracks,
  version,
}: {
  player: StreamingPlayer
  tracks: TrackMeta[]
  /** Bumped by the parent whenever player state changes, to force a re-render. */
  version: number
}) {
  const [, setTick] = useState(0)

  // The playhead moves on the audio clock, which React knows nothing about, so
  // poll it while playing. Deliberately a timer rather than requestAnimationFrame:
  // rAF stops entirely in a hidden or backgrounded tab, which would freeze the
  // readout even though audio keeps playing.
  useEffect(() => {
    if (!player.isPlaying) return
    const id = window.setInterval(() => setTick((t) => t + 1), 80)
    return () => window.clearInterval(id)
  }, [player, player.isPlaying, version])

  const duration = player.duration
  const current = player.currentTime
  const ready = player.readyTime
  const pct = (x: number) => (duration > 0 ? Math.min(100, (x / duration) * 100) : 0)

  const available = tracks.filter((t) => player.hasTrack(t.id))
  const activeMeta = available.find((t) => t.id === player.active)

  return (
    <div className="ab-player">
      <div className="ab-toggle">
        {tracks.map((t) => (
          <button
            key={t.id}
            className={`ab-btn${t.id === player.active ? ' active' : ''}`}
            disabled={!player.hasTrack(t.id)}
            onClick={() => player.switchTo(t.id)}
            title={player.hasTrack(t.id) ? `Switch to ${t.label}` : 'Not ready yet'}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="transport">
        <button
          className="play-btn"
          onClick={() => (player.isPlaying ? player.pause() : player.play())}
          disabled={available.length === 0}
        >
          {player.isPlaying ? '❚❚' : '▶'}
        </button>
        <span className="time">{fmt(current)}</span>
        <div
          className="scrub"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            player.seek(((e.clientX - r.left) / r.width) * duration)
          }}
        >
          {/* How much of this track has been separated so far. */}
          <div className="scrub-ready" style={{ width: `${pct(ready)}%` }} />
          <div className="scrub-play" style={{ width: `${pct(current)}%` }} />
        </div>
        <span className="time">{fmt(duration)}</span>
      </div>

      <div className="ab-hint">
        {activeMeta?.hint ?? 'Switch tracks while playing — the playhead is preserved.'}
      </div>
    </div>
  )
}

function fmt(sec: number): string {
  if (!isFinite(sec)) return '0:00'
  const s = Math.floor(sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
